import type { IntentName } from "@/design";

/**
 * Minutes between `from` and `now`, floored. `null` when there is nothing to
 * measure (no timestamp yet — e.g. Sino has never called, or a window metric
 * is empty).
 */
export function ageMinutes(from: string | null, now: Date): number | null {
  if (from === null) return null;
  const diffMs = now.getTime() - new Date(from).getTime();
  return Math.floor(diffMs / 60_000);
}

/**
 * Status-badge intent for a staleness value, per the spec's client-side
 * thresholds (e.g. "last confirm pick" turns warning after 30 min, danger
 * after 2 h). `null` (nothing recorded yet) is `neutral` — absence is not
 * itself a danger signal; the empty state around it carries that meaning.
 */
export function ageIntent(
  from: string | null,
  now: Date,
  warnMin: number,
  dangerMin: number,
): IntentName {
  const mins = ageMinutes(from, now);
  if (mins === null) return "neutral";
  if (mins >= dangerMin) return "danger";
  if (mins >= warnMin) return "warning";
  return "neutral";
}

/**
 * Human "age" label for a timestamp: "2m ago" / "1h 6m ago" / "3d ago" / "—".
 * Thin by design — all wording lives in `dashboard:age.*`; this just picks
 * the bucket and the interpolation values.
 */
export function formatAge(
  from: string | null,
  now: Date,
  t: (key: string, options?: Record<string, unknown>) => string,
): string {
  const mins = ageMinutes(from, now);
  if (mins === null) return t("dashboard:age.none");
  if (mins < 1) return t("dashboard:age.just_now");
  if (mins < 60) return t("dashboard:age.minutes", { n: mins });
  if (mins < 1440) {
    return t("dashboard:age.hours", { h: Math.floor(mins / 60), m: mins % 60 });
  }
  return t("dashboard:age.days", { n: Math.floor(mins / 1440) });
}
